import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import {
  ArrowLeft,
  Smartphone,
  Monitor,
  Download,
  BarChart2,
  Briefcase,
  ShoppingBag,
  DollarSign,
  Layers,
  Camera,
  TrendingUp,
  TrendingDown,
  Zap,
  Play,
  FileText,
  PieChart,
  Users,
  Bell,
  Globe,
  Lock,
  BarChart3,
  Package,
  CreditCard,
  Search,
  AlertTriangle,
  Building2,
  Settings,
  Upload,
  BookOpen,
  ShoppingCart,
  Shield,
  CheckCircle,
  HelpCircle,
  Factory,
  Boxes,
  Bolt,
  Cpu,
  Wrench,
  BedDouble,
  PartyPopper,
  Gamepad2,
  CalendarCheck,
  Truck,
} from 'lucide-react';
import appDetailData, { type AppDetailData } from '../data/appDetails';
import { getDownloadableApp, POS32_HINT } from '../data/downloads';

// ─── Bouton de téléchargement discret (apps téléchargeables uniquement) ────────
function HeroDownload({ appId }: { appId: string }) {
  const dl = getDownloadableApp(appId);
  if (!dl) return null;
  return (
    <div className="mt-6 inline-flex flex-wrap items-center gap-2 rounded-xl bg-white/10 backdrop-blur px-3 py-2 border border-white/15">
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-white/70 pr-1">
        <Download className="w-3.5 h-3.5" />
        Télécharger
      </span>
      {dl.downloads.android && (
        <a
          href={dl.downloads.android}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-medium px-2.5 py-1.5 transition-colors"
        >
          <Smartphone className="w-3.5 h-3.5" />
          Android
        </a>
      )}
      {dl.downloads.windows && (
        <a
          href={dl.downloads.windows}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-medium px-2.5 py-1.5 transition-colors"
        >
          <Monitor className="w-3.5 h-3.5" />
          Windows
        </a>
      )}
      {dl.downloads.androidPos32 && (
        <a
          href={dl.downloads.androidPos32}
          target="_blank"
          rel="noopener noreferrer"
          title={POS32_HINT}
          className="inline-flex items-center gap-1.5 rounded-lg hover:bg-white/10 text-white/70 hover:text-white text-xs px-2 py-1.5 transition-colors"
        >
          <Smartphone className="w-3.5 h-3.5" />
          Terminal de caisse (32 bits)
        </a>
      )}
    </div>
  );
}

// ─── Icon registry ────────────────────────────────────────────────────────────
const ICON_MAP: Record<string, React.ElementType> = {
  BarChart2, Briefcase, ShoppingBag, DollarSign, Layers, Camera,
  TrendingUp, TrendingDown, Zap, FileText, PieChart, Users, Bell,
  Globe, Lock, BarChart3, Package, CreditCard, Search, AlertTriangle,
  Building2, Settings, Upload, BookOpen, ShoppingCart, Shield, CheckCircle,
  Factory, Boxes, Bolt, Cpu, Wrench, BedDouble, PartyPopper, Gamepad2,
  CalendarCheck, Truck,
};

function resolveIcon(name: string): React.ElementType {
  return ICON_MAP[name] ?? HelpCircle;
}

// ─── Hero gradient overlay ────────────────────────────────────────────────────
function HeroSection({ app }: { app: AppDetailData }) {
  const Icon = resolveIcon(app.iconName);
  return (
    <div className={`relative w-full bg-gradient-to-br ${app.heroGradient} pt-24 pb-20 overflow-hidden`}>
      {/* Decorative circles */}
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        <div className="absolute -top-16 -right-16 w-80 h-80 rounded-full bg-white/5 blur-3xl" />
        <div className="absolute bottom-0 left-1/4 w-64 h-64 rounded-full bg-white/5 blur-2xl" />
      </div>

      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <Link
          to="/applications"
          className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-sm mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Toutes les applications
        </Link>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <div className={`${app.iconBg} w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg`}>
            <Icon className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
              {app.name}
            </h1>
            <p className="mt-2 text-base sm:text-lg text-white/75 max-w-xl leading-relaxed">
              {app.tagline}
            </p>
          </div>
        </div>

        {/* Badges */}
        {app.badges && app.badges.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2">
            {app.badges.map((b) => (
              <span
                key={b.label}
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${b.color} ${b.bg}`}
              >
                {b.label}
              </span>
            ))}
          </div>
        )}

        {/* Highlights */}
        {app.highlights && app.highlights.length > 0 && (
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {app.highlights.map((h) => (
              <li key={h} className="flex items-start gap-2 text-white/80 text-sm">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-white/60" />
                <span>{h}</span>
              </li>
            ))}
          </ul>
        )}

        {/* Téléchargement discret (uniquement pour les apps téléchargeables) */}
        <HeroDownload appId={app.id} />
      </div>
    </div>
  );
}

// ─── Features grid ────────────────────────────────────────────────────────────
function FeaturesSection({ app }: { app: AppDetailData }) {
  return (
    <section className="py-16 bg-white">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-8">
          Fonctionnalités principales
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {app.features.map((f) => {
            const FIcon = resolveIcon(f.iconName);
            return (
              <div
                key={f.title}
                className="flex gap-4 p-5 rounded-2xl border border-gray-100 bg-gray-50 hover:border-gray-200 hover:bg-white transition-all duration-150 group"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary-50 border border-primary-100 shadow-sm flex items-center justify-center group-hover:shadow">
                  <FIcon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">{f.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Demo video section ───────────────────────────────────────────────────────
function DemoVideoSection({ app }: { app: AppDetailData }) {
  return (
    <section className="py-16 bg-gray-50 border-t border-gray-100">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
            Démonstration
          </h2>
          <p className="text-gray-500 text-sm">
            Découvrez {app.name} en action — visite guidée en moins de 3 minutes.
          </p>
        </div>

        {/* Video container */}
        <div className="relative w-full rounded-2xl overflow-hidden shadow-xl border border-gray-200 bg-gray-900" style={{ aspectRatio: '16/9' }}>
          {app.videoId ? (
            <iframe
              src={`https://www.youtube.com/embed/${app.videoId}?rel=0&modestbranding=1`}
              title={`Démo ${app.name}`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
            />
          ) : (
            /* Placeholder when no video exists yet */
            <div className={`absolute inset-0 bg-gradient-to-br ${app.heroGradient} flex flex-col items-center justify-center gap-4`}>
              <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur flex items-center justify-center border-2 border-white/40 cursor-default">
                <Play className="w-6 h-6 text-white ml-0.5" />
              </div>
              <p className="text-white/80 text-sm font-medium">Vidéo de démo disponible prochainement</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function AppDetail() {
  const { id } = useParams<{ id: string }>();

  if (!id || !appDetailData[id]) {
    return <Navigate to="/applications" replace />;
  }

  const app = appDetailData[id];

  return (
    <div className="min-h-screen bg-white">
      <HeroSection app={app} />
      <FeaturesSection app={app} />
      <DemoVideoSection app={app} />
    </div>
  );
}
